import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Panel, Switch, Button } from '@/ui';
import { useDiscussions } from '@/hooks/useDiscussions';
import { discussionsService, type DiscussionPrefs } from '@/core/services/discussionsService';
import { Choice, Hint } from './styles';
import { FollowedDiscussions } from './FollowedDiscussions';
import { InvitationPreference } from '@/features/discussion-tools/DiscussionLibrary';

export function DiscussionSettings({ notifications = false }: { notifications?: boolean }) {
  const { owner, prefs, ready, error: loadError, refresh } = useDiscussions();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [manage, setManage] = useState(false);
  if (!owner) return null;
  const rows: [keyof DiscussionPrefs, string, string][] = notifications
    ? [
        [
          'inApp',
          'In-app discussion notifications',
          'Grouped updates from discussions set to Notify me.',
        ],
        [
          'push',
          'Push discussion notifications',
          'Send the first alert in a group to your registered devices.',
        ],
      ]
    : [
        [
          'showHome',
          'Show discussions you follow on Home',
          'A separate section above your normal feed.',
        ],
        [
          'autoFollow',
          'Automatically follow discussions I reply to',
          'Follow silently after future successful replies. Explicitly unfollowed discussions stay unfollowed.',
        ],
        [
          'rememberPosition',
          'Remember my reading position',
          'Private to you. Turning this off clears saved positions.',
        ],
      ];
  async function save(patch: Partial<DiscussionPrefs>) {
    if (!owner || busy) return;
    setBusy(true);
    setError('');
    try {
      await discussionsService.preferences(owner, patch);
    } catch {
      setError('Could not save this setting. Please try again.');
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <Panel>
        <h3>{notifications ? 'Followed discussions' : 'Conversations'}</h3>
        <Hint>
          {notifications
            ? 'Silent follows never send alerts. Post and comment follows covering the same reply produce one discussion alert.'
            : 'Keep up with ordinary posts and comments you care about.'}
        </Hint>
        {rows.map(([key, label, description]) => (
          <Choice as="div" key={key}>
            <span style={{ flex: 1 }}>
              <strong>{label}</strong>
              <small>{description}</small>
            </span>
            <Switch
              label={label}
              checked={prefs[key] === true}
              disabled={busy || !ready || !!loadError}
              onChange={(value) => void save({ [key]: value })}
            />
          </Choice>
        ))}
        {!notifications && (
          <>
            <InvitationPreference key={owner} />
            <Choice>
              Default comment order{' '}
              <select
                aria-label="Default comment order"
                disabled={busy || !ready || !!loadError}
                value={prefs.commentSort}
                onChange={(e) =>
                  void save({ commentSort: e.target.value as DiscussionPrefs['commentSort'] })
                }
              >
                <option value="most-relevant">Most relevant</option>
                <option value="most-liked">Most cherished</option>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
              </select>
            </Choice>
            <Hint>
              <Link to="/settings/notifications">
                Choose how discussion notifications reach you
              </Link>
            </Hint>
            <Button variant="secondary" aria-expanded={manage} onClick={() => setManage(!manage)}>
              {manage ? 'Close managed discussions' : 'Manage followed discussions'}
            </Button>
          </>
        )}
        {(error || loadError) && <p role="alert">{error || loadError}</p>}
        {loadError && (
          <Button variant="ghost" onClick={() => void refresh()}>
            Try again
          </Button>
        )}
      </Panel>
      {manage && <FollowedDiscussions manage />}
    </>
  );
}
